import co from 'co';
import 'url-search-params-polyfill';
import HttpClient from './HttpClient';
import ApiContentType from './ApiContentType';
import Promise from 'bluebird';

// キャンセルの許容設定
Promise.config({ cancellation: true });

/**
 * API実行の基底クラス
 */
export default class ApiExecutor {
  /**
   * コンストラクタ
   * @param {*} api
   */
  constructor(api) {
    this.api = api;
  }

  /**
   * GET
   * @param {String} path
   * @param {Object} parameter
   * @param {Object} options
   * @param {Object} headers
   */
  get(path, parameter = {}, headers, options = { retry: 2, interval: 1000 }) {
    const apiPath = this.api + path;
    if (!parameter['datum']) parameter['datum'] = 'wgs84';
    let retryCount = 0;
    return new Promise((resolve, reject) => {
      co(function* retryApi() {
        while (retryCount <= options.retry) {
          try {
            const res = yield ApiExecutor.query('get', apiPath, parameter, null, null, headers);
            return res;
          } catch (e) {
            if (retryCount >= options.retry) throw new Error(e);
            retryCount += 1;
            yield ApiExecutor.sleep(options.interval);
          }
        }
        throw new Error(`retry API: "${apiPath}" but failed.`);
      })
      .then(res => resolve(res))
      .catch(err => reject(err));
    });
  }

  /**
   * POST
   * @param {String} path
   * @param {Object} parameter
   * @param {Object} requestBody
   * @param {Object} options
   */
  post(path, parameter, requestBody, contentType = ApiContentType.FORM, headers, options = { retry: 0, interval: 2000 }) {
    const apiPath = this.api + path;
    let retryCount = 0;
    return new Promise((resolve, reject) => {
      co(function* retryApi() {
        while (retryCount <= options.retry) {
          try {
            const res = yield ApiExecutor.query('post', apiPath, parameter, requestBody, contentType, headers);
            return res;
          } catch (e) {
            if (retryCount >= options.retry) throw e;
            retryCount += 1;
            yield ApiExecutor.sleep(options.interval);
          }
        }
        throw new Error();
      })
      .then(res => resolve(res))
      .catch(err => reject(err));
    });
  }

  /**
   * PUT
   * @param {String} path
   * @param {Object} parameter
   * @param {Object} requestBody
   * @param contentType
   * @param headers
   * @param {Object} options
   */
  put(path, parameter, requestBody, contentType = ApiContentType.FORM, headers, options = { retry: 0, interval: 2000 }) {
    const apiPath = this.api + path;
    let retryCount = 0;
    return new Promise((resolve, reject) => {
      co(function* retryApi() {
        while (retryCount <= options.retry) {
          try {
            const res = yield ApiExecutor.query('put', apiPath, parameter, requestBody, contentType, headers);
            return res;
          } catch (e) {
            if (retryCount >= options.retry) throw new Error(e);
            retryCount += 1;
            yield ApiExecutor.sleep(options.interval);
          }
        }
        throw new Error();
      })
      .then(res => resolve(res))
      .catch(err => reject(err));
    });
  }

  /**
   * DELETE
   * @param {String} path
   * @param {Object} parameter
   * @param {Object} options
   */
  delete(path, parameter, headers, options = { retry: 0, interval: 2000 }) {
    const apiPath = this.api + path;
    let retryCount = 0;
    return new Promise((resolve, reject) => {
      co(function* retryApi() {
        while (retryCount <= options.retry) {
          try {
            const res = yield ApiExecutor.query('delete', apiPath, parameter, null, null, headers);
            return res;
          } catch (e) {
            if (retryCount >= options.retry) throw new Error(e);
            retryCount += 1;
            yield ApiExecutor.sleep(options.interval);
          }
        }
        throw new Error();
      })
      .then(res => resolve(res))
      .catch(err => reject(err));
    });
  }

  /**
   * クエリ実行
   * @param {String} path
   * @param {Object} parameter
   */
  static query(method, url, params, body, contentType, headers) {
    const queryData = {
      method,
      url,
      params,
      headers,
    };
    if (method === 'post' || method === 'put') {
      if (contentType === ApiContentType.FORM) {
        queryData.data = new URLSearchParams(body);
      }
      if (contentType === ApiContentType.JSON) {
        queryData.data = body;
      }
      if (contentType === ApiContentType.IMAGE) {
        queryData.headers['Content-Type'] = 'multipart/form-data';
        queryData.data = body;
      }
      if (contentType === ApiContentType.PNG) {
        queryData.headers['Content-Type'] = 'image/png';
        queryData.data = body;
        queryData.responseType = 'arrayBuffer';
      }
    }
    return new Promise((resolve, reject) => {
      HttpClient(queryData)
      .then(res => resolve(res))
      .catch(err => reject(err));
    });
  }

  /**
   * スリープ
   * @param {Object} time
   */
  static sleep(time) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), time);
    });
  }
}
